import { useRouter } from 'next/router'
import { useMemo, useState } from 'react'
import { clientConfig } from '../client'
import {
  DebuggerProvider,
  HeaderProvider,
  HeroProvider,
  LoaderProvider,
  OverlayProvider,
  PageProvider,
  PageViewProvider,
  ThemeProvider,
  ExternalPathsProvider,
  NavigationProvider,
  useAdBlockDetect,
} from '../contexts'
import { UserActivityProvider } from '../contexts/UserActivityContext'
import { PageViewInDayProvider } from '../contexts/hooks/usePageViewInDay'
import { GoogleAdsProvider } from '../contexts/providers/GoogleAdsProvider/GoogleAdsContext'
import { ReelsBBProvider } from '../contexts/providers/ReelsBBProvider'
import { IPage } from '../types'
import { prettyLog } from '../utils'
import PlayerContextProvider from './Player/Store/PlayerContext'
import { TaboolaProvider } from './Taboola/useTaboola'
import { IGoogleAdConfig } from '../sharedComponents/types'

type Props = {
  page: IPage
  googleAdsConfig?: IGoogleAdConfig
  children: React.ReactNode
}

export const Providers = ({ page, children, googleAdsConfig }: Props) => {
  const [thePage, setThePage] = useState<IPage>(page)
  const { asPath } = useRouter()

  useMemo(() => {
    const setNewPage = async () => {
      const fetchPreview = async () => {
        const response = await fetch(
          clientConfig.dataQuerySource + asPath + '&qdata=json'
        )

        if (!response.ok) {
          prettyLog('PageProvider', response, 'error')
          return false
        }

        return response.json()
      }
      if (asPath.includes('preview=true')) {
        const fetchResponse = await fetchPreview()

        if (fetchResponse) {
          setThePage({
            ...fetchResponse,
            isPreview: true,
            isDarkTheme: [
              'live',
              'gridvod',
              'hpvod',
              'tvguide',
              'genresvod',
            ].includes(page.PageMeta.pageType),
          })
          return
        }
      }
      setThePage({
        ...page,
        isPreview: false,
        isDarkTheme: [
          'live',
          'gridvod',
          'hpvod',
          'tvguide',
          'genresvod',
        ].includes(page.PageMeta.pageType),
        isPlayerEmbed: asPath.match(/^\/embed\//) !== null,
      })
    }

    setNewPage()
  }, [page, asPath])

  // AdBlock detection - re-runs on every page entry (incl. SPA)
  useAdBlockDetect()

  return (
    <NavigationProvider>
      <UserActivityProvider>
        <DebuggerProvider>
          <PageViewProvider>
            <PageViewInDayProvider>
              <TaboolaProvider page={thePage}>
                <OverlayProvider>
                  <LoaderProvider>
                    <PlayerContextProvider>
                      <PageProvider page={thePage}>
                        <ThemeProvider page={thePage}>
                          <HeaderProvider>
                            <GoogleAdsProvider config={googleAdsConfig}>
                              <ReelsBBProvider pageMeta={thePage.PageMeta}>
                                <ExternalPathsProvider>
                                  <HeroProvider>{children}</HeroProvider>
                                </ExternalPathsProvider>
                              </ReelsBBProvider>
                            </GoogleAdsProvider>
                          </HeaderProvider>
                        </ThemeProvider>
                      </PageProvider>
                    </PlayerContextProvider>
                  </LoaderProvider>
                </OverlayProvider>
              </TaboolaProvider>
            </PageViewInDayProvider>
          </PageViewProvider>
        </DebuggerProvider>
      </UserActivityProvider>
    </NavigationProvider>
  )
}
